/**
 * useInstructionDispatch — routes agent response metadata → canvas surface.
 *
 * WHY here and not in the canvas (DR-11):
 * - The agent can return instructions at any time (streaming, future SSE).
 *   Having a single host-side dispatcher means every response path (blocking
 *   HTTP today, SSE tomorrow) is handled in one place.
 * - esaBusManager already routes per surfaceId — no new infrastructure needed.
 *
 * Queue + TTL pattern (DR-12):
 * - If the agent responds before the canvas has mounted (e.g. fast network +
 *   slow MF load), we queue the instruction for up to 5s while polling the
 *   awareness store for a surfaceId. After 5s the instruction is dropped to
 *   avoid stale state.
 */

import type { CanvasInstruction, CanvasInstructionType } from '@growthaxl/web-foundation';
import { getOrCreateAwarenessStore } from '../../Canvas/store/canvasAwarenessStore';
import { esaBusManager } from '../esaBus';
import { INSTRUCTION_QUEUE_POLL_MS, INSTRUCTION_TTL_MS } from '../../../constants/timing';

/**
 * Type guard: checks if metadata contains a valid CanvasInstruction.
 */
export function hasCanvasInstruction(
  metadata: Record<string, unknown>
): metadata is { instruction: CanvasInstruction } {
  if (!metadata || typeof metadata !== 'object') return false;
  const instr = (metadata as Record<string, unknown>)['instruction'];
  if (!instr || typeof instr !== 'object') return false;
  return typeof (instr as Record<string, unknown>)['type'] === 'string';
}

function resolveCurrentSurfaceId(): string | null {
  try {
    const store = getOrCreateAwarenessStore();
    return store?.getState().viewport.surfaceId ?? null;
  } catch {
    return null;
  }
}

function buildESAInstruction(instr: CanvasInstruction) {
  return {
    kind: 'CANVAS' as const,
    type: instr.type as CanvasInstructionType,
    payload: instr.payload ?? {},
    requestId: instr.requestId,
  };
}

/**
 * Detects an instruction in converse response metadata and dispatches it
 * to the active canvas surface via esaBusManager.pushInstruction().
 * Queues up to TTL_MS if no surfaceId is available yet.
 */
export function dispatchInstruction(metadata: Record<string, unknown>): void {
  if (!hasCanvasInstruction(metadata)) return;

  const instruction = metadata.instruction;
  const esaInstruction = buildESAInstruction(instruction);

  // Happy path: surfaceId is already available
  const surfaceId = resolveCurrentSurfaceId();
  if (surfaceId) {
    esaBusManager.pushInstruction(surfaceId, esaInstruction);
    return;
  }

  // Queue path: poll until surfaceId appears or TTL expires
  const startedAt = Date.now();
  const timer = setInterval(() => {
    const elapsed = Date.now() - startedAt;
    if (elapsed >= INSTRUCTION_TTL_MS) {
      clearInterval(timer);
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[ESA] dispatchInstruction: TTL expired, dropping instruction', instruction.type);
      }
      return;
    }

    const sid = resolveCurrentSurfaceId();
    if (sid) {
      clearInterval(timer);
      esaBusManager.pushInstruction(sid, esaInstruction);
    }
  }, INSTRUCTION_QUEUE_POLL_MS);
}
